import { useCallback, useEffect, useRef, useState } from "react";
import { getBinding } from "../../lib/binding";
import type { RealtimeEvent } from "@shared/types/foyer";

/** Wire shape returned by GET /premise/:id/rooms/:rid/care/photos */
export interface RoomPhotoWire {
  photoId: string;
  premiseId: string;
  roomId: string;
  fromEmail: string;
  fromName: string;
  subject: string;
  imageUrl: string;
  caption: string | null;
  status: "pending" | "approved" | "rejected";
  receivedAt: string;
  expiresAt: string;
  archivedAt: string | null;
}

/**
 * Fetches approved photos for the paired room and keeps the list fresh.
 *
 * Strategy:
 *   - Fetch immediately on mount (when `active` is true).
 *   - Re-fetch every 30 s while active (poll - the TV is a dedicated device).
 *   - Also re-fetch when a `photos_changed` realtime event arrives via the
 *     optional `realtimeEvent` prop (wired in by CareScheme).
 *
 * `active` lets callers pause polling when the photos screen isn't visible
 * (saves bandwidth when other tiles are in focus).
 *
 * `previewPremiseId` is set in CMS preview mode (no real device binding).
 * In that case, all approved photos for the premise are shown so staff can
 * see real content in the preview without pairing a device.
 */
export function useRoomPhotos(
  active: boolean,
  realtimeEvent?: RealtimeEvent | null,
  previewPremiseId?: string,
  /** Room-specific preview (from "Open preview" on a device). When absent
   *  in preview mode the caller is a generic/template preview with no room
   *  context - return empty without fetching. */
  previewRoomId?: string
): {
  photos: RoomPhotoWire[];
  loading: boolean;
  fetchError: string | null;
  /** True when rendering a generic (no-room) CMS preview. */
  isGenericPreview: boolean;
} {
  const [photos, setPhotos] = useState<RoomPhotoWire[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchPhotos = useCallback(async () => {
    const binding = getBinding();

    // CMS preview mode - no device binding.
    if (!binding && previewPremiseId) {
      // Generic preview: no room context → don't fetch, just stay empty.
      if (!previewRoomId) return;

      // Device-specific preview: fetch photos for that exact room.
      setLoading(true);
      console.log(
        `[useRoomPhotos] preview fetch for premise=${previewPremiseId} room=${previewRoomId}`
      );
      try {
        const res = await fetch(
          `/api/frontlayer/v1/premise/${previewPremiseId}/care/photos?status=approved&roomId=${encodeURIComponent(previewRoomId)}`
        );
        if (res.ok) {
          const data = (await res.json()) as RoomPhotoWire[];
          console.log(`[useRoomPhotos] got ${data.length} photos`);
          setPhotos(data);
        } else {
          console.warn(
            `[useRoomPhotos] preview fetch failed ${res.status} for room ${previewRoomId}`
          );
        }
      } catch (err) {
        console.warn("[useRoomPhotos] preview fetch error", err);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!binding) return;
    setLoading(true);
    try {
      const headers: Record<string, string> = {};
      if (binding.screenToken) {
        headers["X-Screen-Token"] = binding.screenToken;
      }
      const url = `/api/frontlayer/v1/premise/${binding.premiseId}/rooms/${binding.roomId}/care/photos`;
      console.log(`[useRoomPhotos] TV fetch ${url} (room=${binding.roomId})`);
      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = (await res.json()) as RoomPhotoWire[];
        console.log(`[useRoomPhotos] TV got ${data.length} photos`);
        setFetchError(null);
        setPhotos(data);
      } else {
        const body = await res.text().catch(() => "");
        const msg = `HTTP ${res.status} - ${body.slice(0, 120)}`;
        console.warn(
          `[useRoomPhotos] TV fetch failed ${res.status} for ${url}:`,
          body
        );
        setFetchError(msg);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn("[useRoomPhotos] TV fetch error", err);
      setFetchError(msg);
    } finally {
      setLoading(false);
    }
  }, [previewPremiseId, previewRoomId]);

  // Fetch + start poll when active.
  useEffect(() => {
    if (!active) return;
    void fetchPhotos();
    intervalRef.current = setInterval(fetchPhotos, 30_000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [active, fetchPhotos]);

  // Re-fetch immediately on `photos_changed` realtime event.
  useEffect(() => {
    if (!realtimeEvent || realtimeEvent.type !== "photos_changed") return;
    void fetchPhotos();
  }, [realtimeEvent, fetchPhotos]);

  const isGenericPreview =
    !getBinding() && !!previewPremiseId && !previewRoomId;
  return { photos, loading, fetchError, isGenericPreview };
}
